import { useCallback, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { smsApi } from "../../../api/resources";
import type { EmployeeDetail, VerificationAttempt } from "../../../api/types";
import { Button } from "../../../components/ui";
import { fmtDate } from "../../../utils/labels";
import { LoadState, useLoad } from "../shared";
import { SmsStatusPill, lastActivity } from "../sms/smsUi";

const ACTION_LABEL: Record<string, string> = { SENT: "Code sent", CHECK_OK: "Verified", CHECK_FAIL: "Wrong code", EXPIRED: "Code expired", BLOCKED: "Blocked" };
const ACTION_DOT: Record<string, string> = { SENT: "bg-white", CHECK_OK: "bg-emerald-400", CHECK_FAIL: "bg-signal", EXPIRED: "bg-warn", BLOCKED: "bg-signal" };

export function EmployeeSmsSection({ emp, onChanged }: { emp: EmployeeDetail; onChanged: () => void }) {
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const loader = useCallback(() => smsApi.attempts(emp.id), [emp.id, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<VerificationAttempt[]>(loader);
  const sms = emp.sms;
  const canResend = emp.account_status === "REGISTERED_UNVERIFIED" && !emp.phone_verified && !!emp.phone_e164;
  const resend = async () => {
    setBusy(true);
    try {
      const r = await smsApi.resendVerification(emp.id);
      toast.success(`Verification code sent to ${r.phone_masked}`);
      setReload((x) => x + 1);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
      setReload((x) => x + 1);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section data-testid="employee-sms-section" className="mt-8 rounded-sm border border-white/10 bg-surface p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">SMS & verification</h2>
        {canResend && <Button variant="secondary" className="h-9 px-4 text-[11px]" loading={busy} onClick={resend} data-testid="employee-resend-verification-button">Resend verification code</Button>}
      </div>
      {emp.phone_flag === "PHONE_RELEASED" && (
        <p data-testid="employee-phone-released-flag" className="mb-4 rounded-sm border border-signal/50 bg-signal/10 px-3 py-2 text-sm text-red-200">This employee's phone was released to another account after a conflict. They must verify a number again.</p>
      )}
      <dl className="grid gap-4 sm:grid-cols-4">
        <div><dt className="label-caps text-zinc-500">SMS status</dt><dd className="mt-1">{sms && <SmsStatusPill status={sms.sms_status} testId="employee-sms-status" />}</dd></div>
        <div><dt className="label-caps text-zinc-500">Failed attempts</dt><dd data-testid="employee-failed-attempts" className="mt-1 font-mono text-white">{sms?.failed_verification_attempts ?? 0}</dd></div>
        <div><dt className="label-caps text-zinc-500">Opt-out</dt><dd data-testid="employee-opt-out" className="mt-1 text-sm text-white">{sms?.sms_opted_out ? `Opted out ${fmtDate(sms.sms_opted_out_at)}` : "Subscribed"}</dd></div>
        <div><dt className="label-caps text-zinc-500">Last SMS activity</dt><dd data-testid="employee-last-sms" className="mt-1 text-sm text-white">{lastActivity(sms)}</dd></div>
      </dl>
      <h3 className="label-caps mb-3 mt-8 text-zinc-400">Verification attempts</h3>
      <LoadState error={error} loading={!data && !error} testId="employee-attempts-error" />
      {data && data.length === 0 && <p data-testid="employee-attempts-empty" className="text-sm text-zinc-500">No verification attempts yet.</p>}
      {data && data.length > 0 && (
        <ol data-testid="employee-attempts-timeline" className="space-y-3 border-l border-white/10 pl-5">
          {data.map((a) => (
            <li key={a.id} data-testid={`attempt-${a.id}`} className="relative text-sm">
              <span className={`absolute -left-[25px] top-1.5 h-2 w-2 rounded-full ${ACTION_DOT[a.action] ?? "bg-white"}`} />
              <span className="font-semibold text-white">{ACTION_LABEL[a.action] ?? a.action}</span>
              <span className="text-zinc-500"> · {a.phone_masked} · {fmtDate(a.created_at)}{a.actor_type === "ADMIN" ? " · by admin" : ""}{a.reason ? ` · ${a.reason.replace(/_/g, " ").toLowerCase()}` : ""}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
